import { useEffect, useRef, useState } from "react";
import {
  DocumentArrowUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  SparklesIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import TopBar from "../../components/layout/TopBar";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { ragApi } from "../../api/rag";
import { ragConfigApi } from "../../api/settings";
import { useLookups } from "../../hooks/useLookups";
import { projectsApi } from "../../api/projects";

const CATEGORIES = [
  { value: "general",               label: "General" },
  { value: "iso_standard",          label: "Standard" },
  { value: "engineering_guideline", label: "Engineering Guideline" },
  { value: "datasheet",             label: "Datasheet" },
  { value: "failure_analysis",      label: "Failure Analysis" },
  { value: "project_spec",          label: "Project Specification" },
  { value: "project_story",         label: "Project Story" },
  { value: "competitor_analysis",   label: "Competitor Analysis" },
];

const CATEGORY_COLORS = {
  general:               "bg-gray-100 text-gray-600",
  iso_standard:          "bg-blue-50 text-blue-700",
  engineering_guideline: "bg-bh-50 text-bh-700",
  datasheet:             "bg-purple-50 text-purple-700",
  failure_analysis:      "bg-red-50 text-red-700",
  project_spec:          "bg-amber-50 text-amber-700",
  project_story:         "bg-emerald-50 text-emerald-700",
  competitor_analysis:   "bg-orange-50 text-orange-700",
};

function CategoryBadge({ value }) {
  const label = CATEGORIES.find((c) => c.value === value)?.label ?? value;
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${CATEGORY_COLORS[value] ?? "bg-gray-100 text-gray-600"}`}>
      {label}
    </span>
  );
}

// ── Chunking strategy guide ───────────────────────────────────────────────────

function ChunkingGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-gray-200 text-xs">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
      >
        <span className="font-medium">Chunking strategy by file type</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-gray-100 pt-3">

          <div className="flex gap-2">
            <span className="font-mono bg-bh-50 text-bh-700 border border-bh-200 px-1.5 py-0.5 rounded shrink-0">.md</span>
            <div>
              <p className="font-semibold text-gray-700 mb-0.5">Header-based chunking <span className="text-bh-600 font-normal">(recommended)</span></p>
              <p className="text-gray-500 leading-relaxed">
                Each <code className="bg-gray-100 px-1 rounded">##</code> section becomes one chunk. Pipe-delimited tables (<code className="bg-gray-100 px-1 rounded">| col | col |</code>) are always kept as a single chunk regardless of size. Remaining text that exceeds the chunk size is split by character.
              </p>
              <p className="text-gray-400 mt-1">
                Best practice: one concept per <code className="bg-gray-100 px-1 rounded">##</code> section. Tables stay intact automatically — no special markup needed.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <span className="font-mono bg-gray-100 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded shrink-0">.pdf</span>
            <div>
              <p className="font-semibold text-gray-700 mb-0.5">Character-based chunking</p>
              <p className="text-gray-500 leading-relaxed">
                Text is extracted page by page then split at sentence boundaries up to the configured chunk size. Mathematical formulas and tables lose structure during extraction.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <span className="font-mono bg-gray-100 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded shrink-0">.txt</span>
            <div>
              <p className="font-semibold text-gray-700 mb-0.5">Character-based chunking</p>
              <p className="text-gray-500 leading-relaxed">
                Same as PDF. Prefer <code className="bg-gray-100 px-1 rounded">.md</code> for structured engineering documents.
              </p>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

// ── Upload form ───────────────────────────────────────────────────────────────

function UploadForm({ onUploaded }) {
  const [file, setFile]               = useState(null);
  const [category, setCategory]       = useState("general");
  const [description, setDesc]        = useState("");
  const [machineTypeId, setMachineTypeId] = useState("");
  const [projectId, setProjectId]     = useState("");
  const [uploading, setUploading]     = useState(false);
  const [error, setError]             = useState(null);
  const [success, setSuccess]         = useState(null);
  const [projects, setProjects]       = useState([]);
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  const { types: machineTypes } = useLookups("machine_type");

  useEffect(() => {
    projectsApi.list().then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true); setError(null); setSuccess(null);
    try {
      const { data } = await ragApi.upload(
        file, category, description,
        machineTypeId || null,
        projectId || null,
      );
      setSuccess(`Indexed ${data.document.chunk_count} chunks from "${data.document.filename}"`);
      setFile(null); setDesc(""); setMachineTypeId(""); setProjectId("");
      if (fileRef.current) fileRef.current.value = "";
      onUploaded();
    } catch (e) {
      setError(e?.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Upload Document" subtitle="PDF, TXT, or MD — auto-chunked and indexed" />

      <CardBody className="space-y-4">

        {/* Drop zone */}
        <div
          ref={dropRef}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-bh-400 hover:bg-bh-50/30 transition-colors"
        >
          <DocumentArrowUpIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          {file ? (
            <p className="text-sm font-medium text-bh-700">{file.name}</p>
          ) : (
            <>
              <p className="text-sm text-gray-500">Drag & drop or click to select</p>
              <p className="text-xs text-gray-400 mt-0.5">.pdf, .txt, .md</p>
            </>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.txt,.md" className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>

        {/* Chunking strategy guide */}
        <ChunkingGuide />

        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bh-400"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Machine Type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Machine Type <span className="text-gray-400">(optional — improves retrieval)</span>
          </label>
          <select
            value={machineTypeId}
            onChange={(e) => setMachineTypeId(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bh-400"
          >
            <option value="">— Any machine type —</option>
            {machineTypes.map((mt) => (
              <option key={mt.id} value={mt.id}>{mt.label}</option>
            ))}
          </select>
        </div>

        {/* Project */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Project <span className="text-gray-400">(optional — project-specific knowledge)</span>
          </label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bh-400"
          >
            <option value="">— No project —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Description <span className="text-gray-400">(optional)</span></label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="e.g. ISO 9906:2012 Annex A acceptance tests"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bh-400"
          />
        </div>

        {error   && <p className="text-sm text-red-500 flex items-center gap-1"><ExclamationCircleIcon className="h-4 w-4" />{error}</p>}
        {success && <p className="text-sm text-bh-600 flex items-center gap-1"><CheckCircleIcon className="h-4 w-4" />{success}</p>}

        <Button onClick={submit} loading={uploading} disabled={!file} className="w-full justify-center">
          Upload & Index
        </Button>
      </CardBody>
    </Card>
  );
}

// ── Semantic search test ──────────────────────────────────────────────────────

function SearchPanel() {
  const [query, setQuery]         = useState("");
  const [k, setK]                 = useState(5);
  const [machineTypeId, setMT]    = useState("");
  const [results, setResults]     = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError]         = useState(null);

  const { types: machineTypes } = useLookups("machine_type");

  const doSearch = async () => {
    if (!query.trim()) return;
    setSearching(true); setError(null);
    try {
      const { data } = await ragApi.search(query, k, machineTypeId || null);
      setResults(data);
    } catch (e) {
      setError(e?.response?.data?.error || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Semantic Search Test" subtitle="Verify retrieval quality" />
      <CardBody className="space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder="e.g. NPSH calculation method ISO 9906"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bh-400"
          />
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-gray-500">Top</span>
            <input
              type="number" min={1} max={20} value={k}
              onChange={(e) => setK(Number(e.target.value))}
              className="w-12 border border-gray-200 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:border-bh-400"
            />
          </div>
          <Button onClick={doSearch} loading={searching} icon={MagnifyingGlassIcon}>
            Search
          </Button>
        </div>
        <select
          value={machineTypeId}
          onChange={(e) => setMT(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bh-400"
        >
          <option value="">Filter by machine type (all)</option>
          {machineTypes.map((mt) => (
            <option key={mt.id} value={mt.id}>{mt.label}</option>
          ))}
        </select>

        {error && <p className="text-sm text-red-500">{error}</p>}

        {results && (
          <div className="space-y-2 mt-1">
            {results.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No results found.</p>
            ) : results.map((r, i) => (
              <div key={r.chunk_id} className="border border-gray-100 rounded-xl p-3 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-gray-400">#{i + 1}</span>
                  <CategoryBadge value={r.category} />
                  <span className="text-xs font-medium text-gray-700 truncate">{r.filename}</span>
                  <span className="ml-auto text-xs font-semibold text-bh-600 shrink-0">
                    {(r.similarity * 100).toFixed(1)}%
                  </span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed line-clamp-4">{r.text}</p>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ── Chunk viewer ──────────────────────────────────────────────────────────────

function ChunkViewer({ doc, onClose }) {
  const [chunks, setChunks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ragApi.chunks(doc.id)
      .then(({ data }) => setChunks(data.chunks))
      .catch(() => setChunks([]))
      .finally(() => setLoading(false));
  }, [doc.id]);

  const STRATEGY_LABEL = {
    markdown_headers: { label: "Header split", color: "text-bh-700 bg-bh-50 border-bh-200" },
    character:        { label: "Char split",    color: "text-gray-600 bg-gray-100 border-gray-200" },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full mx-4 overflow-hidden flex flex-col" style={{ maxWidth: 760, maxHeight: "85vh" }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <p className="font-semibold text-gray-900 text-sm">{doc.filename}</p>
            <p className="text-xs text-gray-400 mt-0.5">{chunks?.length ?? "…"} chunks</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors">
            ✕
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-3">
          {loading && <p className="text-sm text-gray-400 text-center py-8">Loading chunks…</p>}
          {!loading && chunks?.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">No chunks — re-index the document first.</p>
          )}
          {chunks?.map((c) => {
            const strat = STRATEGY_LABEL[c.strategy] ?? STRATEGY_LABEL.character;
            return (
              <div key={c.index} className="border border-gray-100 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-400">#{c.index + 1}</span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${strat.color}`}>
                    {strat.label}
                  </span>
                  <span className="text-xs text-gray-400 ml-auto">{c.chars} chars</span>
                </div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed font-sans bg-gray-50 rounded-lg px-3 py-2 max-h-48 overflow-y-auto">
                  {c.text}
                </pre>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Document list ─────────────────────────────────────────────────────────────

function DocumentTable({ docs, onDelete, onReindex }) {
  const [viewingChunks, setViewingChunks] = useState(null);
  if (docs.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="h-8 w-8 text-gray-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400">No documents indexed yet.</p>
      </div>
    );
  }

  return (
    <>
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <th className="px-4 py-2.5 text-left font-semibold">File</th>
            <th className="px-4 py-2.5 text-left font-semibold">Category</th>
            <th className="px-4 py-2.5 text-left font-semibold">Machine Type</th>
            <th className="px-4 py-2.5 text-left font-semibold">Project</th>
            <th className="px-4 py-2.5 text-left font-semibold">Description</th>
            <th className="px-4 py-2.5 text-right font-semibold">Chunks</th>
            <th className="px-4 py-2.5 text-right font-semibold">Indexed</th>
            <th className="px-4 py-2.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {docs.map((doc) => (
            <tr key={doc.id} className="hover:bg-gray-50/50 transition-colors">
              <td className="px-4 py-3 font-medium text-gray-800 max-w-[180px] truncate">
                {doc.filename}
              </td>
              <td className="px-4 py-3">
                <CategoryBadge value={doc.category} />
              </td>
              <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                {doc.machine_type_label || <span className="text-gray-300">—</span>}
              </td>
              <td className="px-4 py-3 text-xs text-gray-500 max-w-[140px] truncate">
                {doc.project_name || <span className="text-gray-300">—</span>}
              </td>
              <td className="px-4 py-3 text-gray-500 text-xs max-w-[180px] truncate">
                {doc.description || "—"}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                {doc.chunk_count}
              </td>
              <td className="px-4 py-3 text-right text-xs text-gray-400 whitespace-nowrap">
                {doc.indexed_at
                  ? new Date(doc.indexed_at).toLocaleDateString()
                  : "—"}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => setViewingChunks(doc)}
                    title="View chunks"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-bh-600 hover:bg-bh-50 transition-colors"
                  >
                    <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => onReindex(doc)}
                    title="Re-index"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-bh-600 hover:bg-bh-50 transition-colors"
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(doc)}
                    title="Delete"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {viewingChunks && (
      <ChunkViewer doc={viewingChunks} onClose={() => setViewingChunks(null)} />
    )}
    </>
  );
}

// ── Embedding status badge ────────────────────────────────────────────────────

function EmbeddingStatus({ status }) {
  if (!status) return null;
  const label = status.provider
    ? { openai: "OpenAI", azure_openai: "Azure OpenAI", mistral: "Mistral" }[status.provider] ?? status.provider
    : "Embeddings";
  return (
    <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border
      ${status.ok
        ? "bg-bh-50 border-bh-200 text-bh-700"
        : "bg-red-50 border-red-200 text-red-700"}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${status.ok ? "bg-bh-500" : "bg-red-500"}`} />
      {status.ok ? `${label} embeddings ready` : (status.error ?? `${label} unavailable`)}
    </div>
  );
}

// ── RAG config ────────────────────────────────────────────────────────────────

const RAG_DEFAULTS = { rag_chunk_size: 600, rag_chunk_overlap: 100, rag_top_k: 4, rag_min_similarity: 0.40 };

const INPUT_CLS = `w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800
  focus:outline-none focus:ring-2 focus:ring-bh-400 bg-white`;

function RagConfigCard() {
  const [form,       setForm]       = useState(RAG_DEFAULTS);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [reindexMsg, setReindexMsg] = useState(null);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    ragConfigApi.get()
      .then(({ data }) => setForm(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: Number(e.target.value) })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const { data } = await ragConfigApi.save(form);
      setForm(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.response?.data?.error || "Save failed");
    } finally { setSaving(false); }
  };

  const handleReindexAll = async () => {
    if (!window.confirm("Re-index all documents with the new chunk settings? This may take a while.")) return;
    setReindexing(true); setReindexMsg(null);
    try {
      const { data } = await ragConfigApi.reindexAll();
      setReindexMsg(`Re-indexed ${data.reindexed} document${data.reindexed !== 1 ? "s" : ""}.`);
    } catch (e) {
      setReindexMsg("Re-index failed: " + (e?.response?.data?.error || "unknown"));
    } finally { setReindexing(false); }
  };

  if (loading) return <p className="text-sm text-gray-400 px-4 py-3">Loading…</p>;

  return (
    <div className="space-y-5 p-1">
      <div className="grid grid-cols-2 gap-6">
        {/* Retrieval */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Retrieval — takes effect immediately
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Top K results</label>
              <input type="number" min="1" max="20" value={form.rag_top_k}
                onChange={set("rag_top_k")} className={INPUT_CLS} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Min similarity (0 – 1)</label>
              <input type="number" min="0" max="1" step="0.05" value={form.rag_min_similarity}
                onChange={set("rag_min_similarity")} className={INPUT_CLS} />
            </div>
          </div>
        </div>

        {/* Chunking */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Chunking — requires re-indexing
          </p>
          <div className="mb-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <ExclamationCircleIcon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            Changes take effect only after re-indexing documents.
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Chunk size (chars)</label>
              <input type="number" min="200" max="4000" step="100" value={form.rag_chunk_size}
                onChange={set("rag_chunk_size")} className={INPUT_CLS} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Overlap (chars)</label>
              <input type="number" min="0" max="500" step="50" value={form.rag_chunk_overlap}
                onChange={set("rag_chunk_overlap")} className={INPUT_CLS} />
            </div>
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      {reindexMsg && <p className="text-sm text-gray-500">{reindexMsg}</p>}

      <div className="flex items-center gap-2 pt-1">
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 rounded-lg bg-bh-600 text-white text-sm font-medium hover:bg-bh-700 disabled:opacity-50 transition-colors">
          {saving ? "Saving…" : saved ? "Saved ✓" : "Save"}
        </button>
        <button onClick={handleReindexAll} disabled={reindexing || saving}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">
          {reindexing ? "Re-indexing…" : "Re-index all documents"}
        </button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function KnowledgeBasePage() {
  const [docs, setDocs]       = useState([]);
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setBusy]           = useState({});  // { docId: true } while reindexing
  const [msg, setMsg]         = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: s }] = await Promise.all([
        ragApi.list(),
        ragApi.status(),
      ]);
      setDocs(d);
      setStatus(s);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    try {
      await ragApi.delete(doc.id);
      setMsg(`Deleted "${doc.filename}"`);
      load();
    } catch (e) {
      setMsg("Delete failed: " + (e?.response?.data?.error || "unknown"));
    }
  };

  const handleReindex = async (doc) => {
    setBusy((b) => ({ ...b, [doc.id]: true }));
    try {
      const { data } = await ragApi.reindex(doc.id);
      setMsg(data.message);
      load();
    } catch (e) {
      setMsg("Re-index failed: " + (e?.response?.data?.error || "unknown"));
    } finally {
      setBusy((b) => ({ ...b, [doc.id]: false }));
    }
  };

  return (
    <>
      <TopBar title="Knowledge Base" />
      <main className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="flex items-start gap-3 bg-bh-50 border border-bh-100 rounded-xl px-4 py-3">
          <SparklesIcon className="h-5 w-5 text-bh-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-bh-800">Retrieval-Augmented Generation (RAG)</p>
            <p className="text-xs text-bh-600 mt-0.5 leading-relaxed">
              Documents indexed here are chunked and embedded via your configured LLM provider.
              At query time the Pump Copilot retrieves the most relevant excerpts and uses them
              to ground its answers in your engineering knowledge base.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <EmbeddingStatus status={status} />
          {msg && (
            <span className="text-xs text-gray-500 bg-gray-100 rounded-lg px-3 py-1.5">
              {msg}
            </span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left column — upload + search */}
          <div className="col-span-1 space-y-6">
            <UploadForm onUploaded={load} />
            <SearchPanel />
          </div>

          {/* Right column — document list */}
          <div className="col-span-2">
            <Card>
              <CardHeader
                title="Indexed Documents"
                subtitle={`${docs.length} document${docs.length !== 1 ? "s" : ""}`}
                action={
                  <Button variant="outline" size="sm" icon={ArrowPathIcon} onClick={load} loading={loading}>
                    Refresh
                  </Button>
                }
              />
              <CardBody>
                <DocumentTable
                  docs={docs}
                  onDelete={handleDelete}
                  onReindex={handleReindex}
                />
              </CardBody>
            </Card>
          </div>
        </div>

        {/* RAG config */}
        <Card>
          <CardHeader
            title="Retrieval & Chunking Config"
            subtitle="Controls how documents are split at index time and retrieved at query time"
            icon={<Cog6ToothIcon className="h-4 w-4 text-gray-400" />}
          />
          <CardBody>
            <RagConfigCard />
          </CardBody>
        </Card>

      </main>
    </>
  );
}
